import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DcRuntimeApi } from '@api/bff/openapi/endpoints/dc-runtime-api';
import type {
  UpdateDcRuntimeDto,
  DcRuntimeViewModel
} from '@api/bff/openapi/models';
import { bffApiClient } from '@api/bff/client';
import { db } from '@db/database';
import { dcRuntimeKeys } from '../constants';

type UpdateDcRuntimeParams = {
  id: string;
  dto: UpdateDcRuntimeDto;
};

type MutationContext = {
  previousData: DcRuntimeViewModel | undefined;
};

export const useUpdateDcRuntime = () => {
  const queryClient = useQueryClient();
  const dcRuntimeApi = new DcRuntimeApi(undefined, undefined, bffApiClient);

  return useMutation<
    DcRuntimeViewModel,
    Error,
    UpdateDcRuntimeParams,
    MutationContext
  >({
    mutationFn: async ({ id, dto }: UpdateDcRuntimeParams) => {
      // Preserve eventNum: if the DTO doesn't have one, pull from cache
      if (dto.eventNum == null) {
        const cached = queryClient.getQueryData<DcRuntimeViewModel>(
          dcRuntimeKeys.byId(id)
        );
        if (cached?.eventNum != null) {
          dto = { ...dto, eventNum: cached.eventNum };
        }
      }

      const response = await dcRuntimeApi.apiDcRuntimeIdPut({
        id,
        updateDcRuntimeDto: dto
      });
      return response.data;
    },

    onMutate: async ({ id, dto }) => {
      await queryClient.cancelQueries({
        queryKey: dcRuntimeKeys.byId(id)
      });

      const previousData = queryClient.getQueryData<DcRuntimeViewModel>(
        dcRuntimeKeys.byId(id)
      );

      if (previousData) {
        queryClient.setQueryData(dcRuntimeKeys.byId(id), {
          ...previousData,
          ...dto,
          jsonData: dto.jsonData ?? previousData.jsonData,
          eventNum: dto.eventNum ?? previousData.eventNum
        });
      }

      return { previousData };
    },

    onSuccess: (data, variables) => {
      if (data && variables.id) {
        queryClient.setQueryData(dcRuntimeKeys.byId(variables.id), data);

        // Also update the byWoDetailGlobalId cache if the response has one
        const woDetailGlobalId = (data as any).woDetailGlobalId;
        if (woDetailGlobalId) {
          queryClient.setQueryData(
            dcRuntimeKeys.byWoDetailGlobalId(woDetailGlobalId),
            data
          );

          // Write back to IndexedDB for offline access
          db.cachedCalibrationRuntimes
            .where('woDetailGlobalId')
            .equals(woDetailGlobalId)
            .first()
            .then(existing => {
              if (existing) {
                return db.cachedCalibrationRuntimes.update(existing.id!, {
                  ...data,
                  cachedAt: new Date().toISOString(),
                  _userId: existing._userId
                });
              }
            })
            .catch(() => {
              // Non-critical — don't fail the mutation for cache update issues
            });
        }
      }
    },

    onError: (_error, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          dcRuntimeKeys.byId(variables.id),
          context.previousData
        );
      }
    }
  });
};
